import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ResourceCard from '@/components/feature/ResourceCard';
import Pagination from '@/components/feature/Pagination';
import { getCategoryById, filterResources, paginate, totalPages } from '@/lib/catalog';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 6;

const RANGE_OPTIONS = [
  { value: 'all', label: '全部时间' },
  { value: '7d', label: '最近 7 天' },
  { value: '30d', label: '最近 30 天' },
  { value: '90d', label: '最近 90 天' },
];

const SORT_OPTIONS = [
  { value: 'latest', label: '最新发布' },
  { value: 'popular', label: '最多播放' },
  { value: 'favorites', label: '最多收藏' },
];

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const sub = searchParams.get('sub') || 'all';
  const range = searchParams.get('range') || 'all';
  const sort = searchParams.get('sort') || 'latest';
  const page = Math.max(1, Number(searchParams.get('page') || '1'));

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === 'all') next.delete(key);
      else next.set(key, value);
    });
    next.delete('page');
    setSearchParams(next);
  };

  const goPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const results = useMemo(
    () =>
      category
        ? filterResources({
            category: category.id,
            subCategory: sub === 'all' ? undefined : sub,
            timeRange: range,
            sort,
          })
        : [],
    [category, sub, range, sort],
  );

  useSeo({
    title: category ? `${category.name} - 免费91` : '分类不存在 - 免费91',
    description: category
      ? `${category.name}频道汇聚海量免费${category.name.replace('免费', '')}资源，${category.description}`
      : '该分类暂不存在，请返回首页浏览其他免费资源。',
    keywords: category
      ? `免费${category.name.replace('免费', '')},${category.subCategories.map((item) => item.name).join(',')},免费91`
      : '免费91,免费资源',
  });

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-400 items-center justify-center text-3xl">
          <i className="ri-emotion-sad-line"></i>
        </span>
        <h1 className="mt-5 font-heading font-semibold text-2xl text-foreground-950">分类不存在</h1>
        <p className="mt-2 text-foreground-600">你访问的分类可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const pages = totalPages(results.length, PAGE_SIZE);
  const safePage = Math.min(page, pages);
  const items = paginate(results, safePage, PAGE_SIZE);
  const hasFilter = sub !== 'all' || range !== 'all' || sort !== 'latest';

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 py-8">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{category.name}</span>
        </nav>

        <div className="flex items-start gap-4">
          <span className="w-14 h-14 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
            <i className={`${category.icon} text-3xl`}></i>
          </span>
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-2 text-foreground-600 leading-relaxed">{category.description}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8">
        <div className="flex gap-2 overflow-x-auto f91-scroll-hidden pb-1">
          <button
            type="button"
            onClick={() => updateParams({ sub: 'all' })}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              sub === 'all'
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {category.subCategories.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => updateParams({ sub: item.id })}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                sub === item.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>

        <div className="mt-4 rounded-lg bg-background-100 border border-background-200 p-3 md:p-4">
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="w-full flex items-center justify-between md:hidden text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-filter-3-line"></i>
              筛选与排序
            </span>
            <i className={filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
          </button>

          <div className={`${filtersOpen ? 'block' : 'hidden'} md:block mt-3 md:mt-0`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 shrink-0">发布时间</span>
                {RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateParams({ range: option.value })}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                      range === option.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-secondary-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 md:ml-auto">
                <span className="text-xs text-foreground-500 shrink-0">排序</span>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(event) => updateParams({ sort: event.target.value })}
                    className="appearance-none h-9 pl-3 pr-8 rounded-md bg-background-50 text-sm text-foreground-800 outline-none cursor-pointer"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </div>
                {hasFilter && (
                  <button
                    type="button"
                    onClick={() => setSearchParams(new URLSearchParams())}
                    className="text-xs text-foreground-500 hover:text-accent-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line"></i>
                    重置
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10">
        <p className="text-sm text-foreground-500 mb-5">
          共找到 <span className="text-primary-700 font-medium">{results.length}</span> 个资源
        </p>

        {items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {items.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 rounded-lg bg-background-100 border border-background-200">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-200 text-foreground-400 items-center justify-center text-3xl">
              <i className="ri-search-eye-line"></i>
            </span>
            <h2 className="mt-5 font-heading font-semibold text-xl text-foreground-950">暂无匹配资源</h2>
            <p className="mt-2 text-foreground-600">换个筛选条件试试，或重置后浏览全部内容。</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams())}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-300 text-sm font-medium transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        )}

        <Pagination page={safePage} totalPages={pages} onChange={goPage} />
      </div>
    </div>
  );
}